import { useState, useEffect, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import { supabase } from '@/integrations/supabase/client';

const BIOMETRIC_SERVER = 'com.vent.app';

export const useBiometricLogin = () => {
  const [hasSavedCredentials, setHasSavedCredentials] = useState(false);
  const [isAvailable, setIsAvailable] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const init = async () => {
      try {
        const { NativeBiometric } = await import('@capgo/capacitor-native-biometric');
        const result = await NativeBiometric.isAvailable();
        setIsAvailable(result.isAvailable);

        if (result.isAvailable) {
          try {
            const creds = await NativeBiometric.getCredentials({ server: BIOMETRIC_SERVER });
            setHasSavedCredentials(!!creds.username);
          } catch {
            // No stored credentials
            setHasSavedCredentials(false);
          }
        }
      } catch {
        setIsAvailable(false);
      }
    };

    init();
  }, []);

  const saveCredentials = useCallback(async (email: string, password: string) => {
    if (!Capacitor.isNativePlatform() || !isAvailable) return;

    try {
      const { NativeBiometric } = await import('@capgo/capacitor-native-biometric');
      await NativeBiometric.setCredentials({
        username: email,
        password,
        server: BIOMETRIC_SERVER,
      });
      setHasSavedCredentials(true);
      console.log('[BiometricLogin] Credentials saved');
    } catch (error) {
      console.error('[BiometricLogin] Failed to save credentials:', error);
    }
  }, [isAvailable]);

  const biometricSignIn = useCallback(async (): Promise<{
    success: boolean;
    user?: any;
    isTherapistOnly?: boolean;
    isAdmin?: boolean;
    isPartner?: boolean;
  }> => {
    if (!Capacitor.isNativePlatform()) return { success: false };

    setLoading(true);
    try {
      const { NativeBiometric } = await import('@capgo/capacitor-native-biometric');

      await NativeBiometric.verifyIdentity({
        reason: 'Sign in to Vent',
        title: 'Sign In',
        subtitle: 'Use Face ID or Touch ID to sign in',
        description: 'Authenticate to access your Vent account.',
      });

      const credentials = await NativeBiometric.getCredentials({ server: BIOMETRIC_SERVER });

      const { data, error } = await supabase.auth.signInWithPassword({
        email: credentials.username,
        password: credentials.password,
      });

      if (error) {
        // If credentials are stale (e.g. password changed), clear them
        await NativeBiometric.deleteCredentials({ server: BIOMETRIC_SERVER });
        setHasSavedCredentials(false);
        return { success: false };
      }

      if (data.user) {
        if ((data.user.app_metadata as Record<string, unknown> | undefined)?.account_type === 'partner') {
          return { success: true, user: data.user, isAdmin: false, isPartner: true };
        }
        // Check therapist-only
        const { data: profileData } = await supabase
          .from('profiles')
          .select('is_therapist_only')
          .eq('user_id', data.user.id)
          .single();

        if (profileData?.is_therapist_only) {
          await supabase.auth.signOut();
          return { success: false, isTherapistOnly: true };
        }

        // Check admin
        const { data: roleData } = await supabase.rpc('has_role', {
          _user_id: data.user.id,
          _role: 'admin',
        });

        return { success: true, user: data.user, isAdmin: roleData === true };
      }

      return { success: false };
    } catch (error) {
      console.error('[BiometricLogin] Sign-in failed:', error);
      return { success: false };
    } finally {
      setLoading(false);
    }
  }, []);

  const clearCredentials = useCallback(async () => {
    if (!Capacitor.isNativePlatform()) return;

    try {
      const { NativeBiometric } = await import('@capgo/capacitor-native-biometric');
      await NativeBiometric.deleteCredentials({ server: BIOMETRIC_SERVER });
      setHasSavedCredentials(false);
      console.log('[BiometricLogin] Credentials cleared');
    } catch {
      // Ignore errors on clear
    }
  }, []);

  return {
    hasSavedCredentials,
    isAvailable,
    loading,
    saveCredentials,
    biometricSignIn,
    clearCredentials,
  };
};
